<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import type { IconLibProps } from '../tools/icon.vue'
	import { ProductCode, type ProductContext } from '~/types'
	import { storeToRefs } from 'pinia'

	import { CoverPeriodMap } from '../../server/utils/constants'

	const props = defineProps<{
		show: boolean
		category: string
		productCode?: number | string
	}>()
	const emits = defineEmits<{ (event: 'update:show', payload: boolean): void }>()
	const { allProducts, allProductCategories } = storeToRefs(useProductsStore())
	const { productCategoryType } = useUtils()
	const { toLower } = StringMethods()
	const { internationalNoFormat } = useCurrency()
	const tabsGroup = computed(() => {
		return allProductCategories.value.map(item => {
			const type = productCategoryType(item.name)
			let iconProps = {}
			if (type?.isHealth) {
				iconProps = { iconProp: { dir: 'fill', icon: 'heart-gray' }, icon: 'heart-active' }
			} else if (type?.isGadget) {
				iconProps = { iconProp: { dir: 'fill', icon: 'gadget-gray' }, icon: 'gadget-active' }
			} else if (type?.isTravel) {
				iconProps = { iconProp: { dir: 'fill', icon: 'plane-gray' }, icon: 'plane-active' }
			} else if (type?.isAuto) {
				iconProps = { iconProp: { dir: 'fill', icon: 'bus-gray' }, icon: 'bus-active' }
			}

			return {
				name: toLower(item.name),
				key: toLower(item.name),
				...iconProps
			}
		})
	})
	const iconProp = (categoryName: string) => {
		const type = productCategoryType(categoryName)
		let iconProps = {} as IconLibProps
		if (type?.isHealth) {
			iconProps = { dir: 'fill', icon: 'heart-rate-pulse' }
		} else if (type?.isGadget) {
			iconProps = { dir: 'random', icon: 'monitor-mobile' }
		} else if (type?.isTravel) {
			iconProps = { dir: 'fill', icon: 'airplane', pathClass: 'fill-white' }
		} else if (type?.isAuto) {
			iconProps = { dir: 'fill', icon: 'bus-orange' }
		}
		return iconProps
	}
	const activeTab = ref('health')
	const categoryType = computed(() => productCategoryType(activeTab.value))
	const currenUrlName = computed(() => toLower(useRoute().name).replace(/-/g, ' '))
	const productPricingContent = computed(() =>
		allProducts.value.filter(item => toLower(item.productCategory.name) === toLower(activeTab.value))
	)
	const openPlan = (arg: ProductContext[number]) => {
		/*
			navigateTo({ path: item.link_name }), emits('update:show', false)
			*/
		let url = `/${toLower(arg.alternate_name).replace(/ /g, '-')}`
		if (arg.code_name === ProductCode.LAPTOP_COVER) {
			url = '/gadget'
		} else if (arg.productCategory?.name === 'Travel') {
			url = '/travel-cover'
		}
		resolve(async () => await navigateTo(url))
		emits('update:show', false)
	}

	watch(
		() => props.show,
		isOpen => {
			if (isOpen) {
				activeTab.value = toLower(props.category || activeTab.value || 'health')
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<ui-modal class="bg-current" :show="show" @update:show="$emit('update:show', false)">
		<div class="px-5 py-10 sm:p-[100px_60px_48px_60px]">
			<div class="flex items-center gap-x-5 w-full justify-center">
				<div
					class="sm:h-[52px] h-[40px] sm:w-[52px] w-[40px] flex-none rounded-full flexed justify-center border border-main items-center"
				>
					<tools-icon dir="internal-logo" icon="mcg-cap-primary" :width="36" />
				</div>

				<h4 class="maitree-semibold sm:text-5xl text-blue-950">Insure to suit your specific needs</h4>
			</div>

			<div class="mt-10">
				<client-only>
					<tools-tab
						:tabs="tabsGroup"
						v-model:active-tab="activeTab"
						:ui="{
							line: { borderColor: 'border-health-500' },
							tab: {
								default: 'sm:text-xl capitalize text-base  flex',
								body: {
									default: 'bg-white flex w-full',
									alignItem: 'items-center lg:justify-center',
									padding: 'py-2 md:py-0 md:pt-5',
									border: 'border-b-[1.5px] border-gray-200'
								},
								wrapper: { default: 'flex w-full' },
								active: {
									color: 'text-gray-800',
									font: 'font-semibold'
								},
								inactive: {
									font: 'font-medium'
								}
							}
						}"
					>
						<template #tab="{ item, active }">
							<div class="inline-flex mx-5 items-center gap-x-2 sm:gap-x-4">
								<tools-icon
									v-if="item?.iconProp"
									v-bind="item.iconProp"
									:icon="active ? item.icon : item.iconProp.icon"
									:height="20"
									:width="20"
								/>

								<span class="truncate">
									{{ item.name }}
								</span>
							</div>
						</template>
					</tools-tab>
				</client-only>

				<div class="bg-white border border-gray-300 sm:p-5 p-3 min-h-[400px] sm:min-h-[607px] mt-10">
					<ui-carousel
						:ui="{ wrapper: { default: productPricingContent.length <= 2 ? 'xl:justify-center' : '' } }"
					>
						<template v-for="(item, index) in productPricingContent" :key="index">
							<ui-pricing-card
								v-if="item.pricingBenefits?.benefit_highlight"
								:benefit_highlight="item.pricingBenefits.benefit_highlight ?? []"
								:name="{
									text: item.alternate_name
								}"
								:description="{
									text: internationalNoFormat(
										[
											ProductCode.FLEXICARE_BASTION_BUSINESS,
											ProductCode.FLEXICARE_BASTION_PERSONAL
										].includes(item.code_name)
											? item.alternative_price || 0
											: (item.price?.market_price ?? '0'),
										'₦'
									)
								}"
								:card_icon="item.pricingBenefits.card_icon"
								:ui="{
									wrapper: { hover: '', padding: 'p-5', bg: 'bg-purple-25' },
									heading: {
										border: '',
										spacer: 'space-y-1.5',
										name: {
											base: [
												'inline-block p-[4px_12px]  text-white rounded-lg mb-3 capitalize',
												categoryType?.isAuto && 'bg-auto-500',
												categoryType?.isHealth && 'bg-health-500',
												categoryType?.isTravel && 'bg-travel-500',
												categoryType?.isGadget && 'bg-gadget-500'
											],
											hover: ''
										}
									},

									list: {
										items: 'pb-2 sm:pb-0 inline-flex gap-3 items-center'
									},
									actionBtn: { base: 'absolute bottom-5  sm:top-[480px] ' }
								}"
								class="flex-none snap-start mx-auto xl:mx-0"
								:btn-label="
									toLower(item.alternate_name) === currenUrlName
										? `Continue with ${item.alternate_name}`
										: 'Learn More'
								"
								@learn-more="openPlan(item)"
							>
								<template #name>
									<div class="flex items-center gap-x-2">
										<tools-icon v-bind="iconProp(item.productCategory.name)" />
										{{ item.alternate_name }}
									</div>
								</template>
								<template #description>
									<p class="text-gray-500 text-sm">
										{{ categoryType?.isHealth ? 'For as low as' : '' }}
										<span class="font-[700] sm:text-[1.875em] text-gray-600 text-lg">
											<span v-show="!categoryType?.isTravel">
												{{
													categoryType?.isGadget
														? `${parseFloat(String(item.market_price))}% Gadget Value`
														: item.is_dynamic_price
															? `${parseFloat(String(item.market_price))}% `
															: internationalNoFormat(item.market_price, '₦')
												}}
											</span>
											<span v-show="categoryType?.isTravel"> Premium based on: </span>
										</span>
										<span v-show="categoryType?.isAuto">
											/ vehicle value {{ CoverPeriodMap[toLower(item.cover_period)] }}
										</span>
										{{ categoryType?.isGadget ? '/ gadget annually' : '' }}
										{{ categoryType?.isHealth ? '/ staff monthly' : '' }}
										{{ categoryType?.isTravel ? 'Duration of travel, Country, Age' : '' }}
									</p>
								</template>
							</ui-pricing-card>
						</template>
					</ui-carousel>
				</div>
			</div>
		</div>
	</ui-modal>
</template>
